<template>

  <v-container fluid class="px-0 pt-0 vehicle-item-edit">

    <!-- (data loading) -->
    <template v-if="isDataLoading">
      <v-row>
        <v-col class="mt-4 d-flex align-center justify-center" style="height:120px">
          <v-progress-circular indeterminate :size="50" color="primary" />
        </v-col>
      </v-row>
    </template>

    <template v-else-if="vehicleToEdit">

      <!-- Breadcrumb -->
      <v-row class="mt-2 mb-6">
        <v-col cols="12" class="d-flex align-center justify-start page-breadcrumb-new">
          <!-- (breadcrumb) -->
          <v-icon small class="mr-2">mdi-car</v-icon>
          <router-link :to="{ name: 'vehicle-list' }">Vehicles</router-link>
          <div class="separator">/</div>
          <div class="active-item" v-if="isNew">New Vehicle</div>
          <div class="active-item" v-else><span class="label-prefix">Edit Vehicle:</span> {{ vehicleToEdit.license_plate }}</div>
        </v-col>
      </v-row>

      <!-- Page Actions (cancel, save) -->
      <v-toolbar flat class="item-controls item-editor-controls">
        <v-btn color="gray" text-color="black" @click="cancel" v-if="isDirty">Cancel</v-btn>
        <v-spacer />
        <v-btn color="primary" height="40" width="110" @click="save" v-if="isDirty">Save</v-btn>
      </v-toolbar>

      <!-- Item Edit Form -->
      <v-container fluid class="mt-4 page-content">
        <v-form lazy-validation ref="form" autocomplete="off" v-model="isFormValid">
          <v-row>
            <v-col cols="12" sm="6 text-h5">
                {{ vehicleToEdit.year }} {{ vehicleToEdit.make }} {{ vehicleToEdit.vehicle_model }}
            </v-col>
          </v-row>

          <!-- License plate -->
          <v-row class="mt-4">
            <v-col cols="8" xs="8" sm="5" md="4" lg="2">
              <v-text-field
                  label="License Plate"
                  required
                  :rules="$root.rules.fieldRequired"
                  v-model.trim="vehicleToEdit.license_plate" />
            </v-col>
          </v-row>

          <!-- Nickname -->
          <v-row class="mt-0">
            <v-col cols="10" xs="10" sm="8" md="5" lg="4">
              <v-text-field
                  label="Nickname (optional)"
                  v-model="vehicleToEdit.nickname" />
            </v-col>
          </v-row>

          <!-- Year -->
          <v-row class="mt-0">
            <v-col cols="5" xs="5" sm="4" md="3" lg="3">
              <v-text-field
                  label="Year"
                  required
                  :rules="$root.rules.fieldRequired"
                  v-model.trim="vehicleToEdit.year" />
            </v-col>
          </v-row>

          <!-- Make -->
          <v-row class="mt-0">
            <v-col cols="9" xs="9" sm="6" md="5" lg="3">
              <v-text-field
                  label="Make"
                  required
                  :rules="$root.rules.fieldRequired"
                  v-model="vehicleToEdit.make" />
            </v-col>
          </v-row>

          <!-- Model -->
          <v-row class="mt-0">
            <v-col cols="9" xs="9" sm="6" md="5" lg="3">
              <v-text-field
                  label="Model"
                  required
                  :rules="$root.rules.fieldRequired"
                  v-model.trim="vehicleToEdit.vehicle_model" />
            </v-col>
          </v-row>

          <!-- Color -->
          <v-row class="mt-0">
            <v-col cols="6" xs="6" sm="5" md="3" lg="3">
              <v-text-field
                  label="Color"
                  required
                  :rules="$root.rules.fieldRequired"
                  v-model.trim="vehicleToEdit.color" />
            </v-col>
          </v-row>

          <!-- VIN number -->
          <v-row class="mt-0">
            <v-col cols="12" xs="12" sm="7" md="5" lg="3">
              <v-text-field
                  label="Vehicle Identification Number"
                  v-model="vehicleToEdit.vin_number" />
            </v-col>
          </v-row>

          <!-- Notes -->
          <v-row class="mt-0">
            <v-col cols="12" xs="12" sm="12" md="5" lg="6">
              <v-textarea
                  rows="1"
                  label="Notes"
                  v-model="vehicleToEdit.notes" />
            </v-col>
          </v-row>
        </v-form>
      </v-container>

    </template>

  </v-container>

</template>

<script>
export default {
  name: 'VehicleItemEditPage',

  props: {
    vehicleId: {
      type: String,
      default: null
    }
  },

  data () {
    return {
      isDataLoading: false,
      isDirty: true,
      isFormValid: true
    }
  },

  computed: {
    // The active item being edited
    vehicleToEdit () {
      return this.$store.getters['sh/itemEditing']('vehicle')
    },
    isNew () {
      return (this.vehicleToEdit && this.vehicleToEdit.id === null)
    }
  },

  async beforeMount () {
    if (!this.vehicleId) {
      // Create draft item for "new"
      const draft = await this.$store.dispatch('sh/createDraft', {
        resource: 'vehicle',
        data: { vehicle_status: 'AT_HQ' }
      })
      this.$store.dispatch('sh/setItemToEdit', draft)
    } else if (!this.vehicleToEdit) {
      // Ensure itemToEdit is loaded
      this.isDataLoading = true

      const vehicle = await this.$store.dispatch('sh/fetchData', {
        resource: 'vehicle',
        id: this.vehicleId
      })
      this.$store.dispatch('sh/setItemToEdit', vehicle)

      this.isDataLoading = false
    }
  },

  beforeDestroy () {
    // Clear editing registry
    this.$store.dispatch('sh/clearItemEditing', 'vehicle')
  },

  methods: {
    cancel () {
      if (this.isNew) this.cancelDraft()
      else this.cancelEdit()
    },

    cancelDraft () {
      this.$root.goTo('vehicle-list') // return to list page
    },

    cancelEdit () {
      this.$root.goTo('vehicle-item', { vehicleId: this.vehicleId }) // go to item view page
    },

    async save () {
      await this.$refs.form.validate()
      if (!this.isFormValid) return false
      if (this.isNew) this.saveDraft()
      else this.saveEdit()
    },

    async saveDraft () {
      await this.$store.dispatch('sh/createData', this.vehicleToEdit) // create item
      this.$root.goTo('vehicle-list') // return to list page
    },

    // TODO - This is temp !!!
    // We will save here, and display results. Then allow the user to close the page.
    // Requires usage of the isDirty pattern.
    async saveEdit () {
      await this.$store.dispatch('sh/updateData', this.vehicleToEdit) // update item
      this.$root.goTo('vehicle-item', { vehicleId: this.vehicleId }) // return to item page
    }
  }
}
</script>

<style scoped>
</style>
